import { useEffect, useRef, type ReactNode } from 'react';
import styles from './Tabs.module.css';

export interface TabDef<Key extends string> {
  key: Key;
  label: string;
  /** Shown after the label, for a count. Omitted rather than rendered as zero. */
  badge?: number;
}

export interface TabsProps<Key extends string> {
  tabs: ReadonlyArray<TabDef<Key>>;
  current: Key;
  onSelect: (key: Key) => void;
  /** Names the tab strip for a screen reader, since a page may hold more than one. */
  label: string;
  children: ReactNode;
}

/**
 * A tab strip, to the ARIA tabs pattern.
 *
 * Real `role="tab"` buttons in a `role="tablist"`, with the panel labelled by
 * its tab, rather than a row of styled buttons: a screen reader announces
 * "tab 2 of 4" and the arrow keys move between them, which is what somebody
 * who cannot see the underline needs in order to know the strip is a strip.
 *
 * Only the selected tab is in the tab order. That is the pattern's own rule and
 * it is the reason the strip is worth using: a keyboard user tabs PAST the
 * whole group in one press rather than through every tab in it.
 *
 * State is the caller's, so a screen that keeps its tab in the URL can, and one
 * that does not need to does not have to.
 *
 * The strip scrolls sideways where its tabs do not fit, and the selected tab is
 * kept inside it. Payments has four since the request of 5 October 2026
 * (third), section 3, and the fourth is past the edge of a 360px phone: opened
 * by its address, or reached with the arrow keys, it would be selected and out
 * of sight.
 */
export function Tabs<Key extends string>({
  tabs,
  current,
  onSelect,
  label,
  children,
}: TabsProps<Key>) {
  const strip = useRef<HTMLDivElement>(null);
  // What the strip draws: a count that arrives late widens its tab and moves the ones after it.
  const drawn = tabs.map((tab) => `${tab.label} ${String(tab.badge ?? 0)}`).join('|');

  // The strip alone is moved, by its own scroll position: the page never jumps to a tab.
  useEffect(() => {
    const box = strip.current;
    const tab = box?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!box || !tab) return;
    const edge = box.getBoundingClientRect();
    const own = tab.getBoundingClientRect();
    if (own.left < edge.left) box.scrollLeft -= edge.left - own.left;
    else if (own.right > edge.right) box.scrollLeft += own.right - edge.right;
  }, [current, drawn]);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const index = tabs.findIndex((tab) => tab.key === current);
    if (index === -1) return;

    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (step === 0) return;

    event.preventDefault();
    // Wraps, which is what the pattern specifies: the end of the strip is not
    // a wall, and a person holding the arrow key should not have to notice.
    const next = tabs[(index + step + tabs.length) % tabs.length];
    if (next) onSelect(next.key);
  }

  return (
    <>
      <div
        ref={strip}
        className={styles.strip}
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab) => {
          const selected = tab.key === current;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              id={`tab-${tab.key}`}
              aria-selected={selected}
              aria-controls={`panel-${tab.key}`}
              tabIndex={selected ? 0 : -1}
              className={`${styles.tab} ${selected ? styles.on : ''}`}
              onClick={() => onSelect(tab.key)}
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className={styles.badge}>{tab.badge}</span>
              )}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`panel-${current}`}
        aria-labelledby={`tab-${current}`}
        className={styles.panel}
      >
        {children}
      </div>
    </>
  );
}
